// src/pages/distribuicao/VendasTransportesEstatisticas.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import DialogFichaTransporteDetalhe from "pages/distribuicao/DialogFichaTransporteDetalhe";
import DialogVendasTransportesOverrideHistorico from "pages/distribuicao/DialogVendasTransportesOverrideHistorico";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// UI base JFB
import PageHeader from "pages/components/PageHeader";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { createAutocompleteFilter } from "pages/utils/autocompleteFilter";

import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import Draggable from "react-draggable";

// MUI
import {
  Card,
  CircularProgress,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";

// ------------------- Helpers -------------------

function CellCenter({ children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        width: "100%",
      }}
    >
      {children}
    </div>
  );
}

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function isoDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function onlyDate(v) {
  if (!v) return "";
  const s = String(v);
  const idx = s.indexOf("T");
  return idx >= 0 ? s.slice(0, idx) : s;
}

function fmtNum(v, dec = 3) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);

  if (dec === 0) {
    return n.toFixed(0);
  }

  return n.toFixed(dec).replace(/\.?0+$/, "");
}

function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}

function fmtInt(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return String(Math.trunc(n));
}

function getCorEficiencia(v) {
  const n = Number(v || 0);

  if (n >= 100) return "#2e7d32"; // verde
  if (n >= 96) return "#66bb6a"; // verde claro
  if (n >= 92) return "#f9a825"; // amarelo
  if (n >= 88) return "#ef6c00"; // laranja
  return "#c62828"; // vermelho
}

function abrirTransportesNovoSeparador(ref, datafim) {
  if (!ref) return;

  const href = `/distribuicao/transportes?reftransporte=${encodeURIComponent(String(ref))}`;

  window.open(href, "_blank", "noreferrer");
}

function groupBy(arr, keyFn) {
  const m = new Map();
  (arr || []).forEach((x) => {
    const k = keyFn(x);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(x);
  });
  return m;
}

const clickableValueStyle = {
  cursor: "pointer",
  fontWeight: 600,
  color: "#1a237e",
  textDecoration: "none",
};

const clickableValueHoverStyle = {
  textDecoration: "underline",
};

export default function VendasTransportesEstatisticas() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const isDistribuicao = temFuncao("Distribuição");

  const distribuicaoNome = useMemo(() => {
    const v = userData?.distribuicaonome ? String(userData.distribuicaonome) : "";
    return v.trim();
  }, [userData]);

  const podeVerPagina = useMemo(() => {
    // adminLike vê tudo; Distribuição só se tiver distribuicaonome preenchido
    return isAdminLike || (isDistribuicao && !!distribuicaoNome);
  }, [isAdminLike, isDistribuicao, distribuicaoNome]);

  const [loading, setLoading] = useState(false);

  const [dataIni, setDataIni] = useState(() => {
    const hoje = new Date();

    // data fim = ontem
    const dataFimTemp = new Date(hoje);
    dataFimTemp.setDate(dataFimTemp.getDate() - 1);

    // início = dia 1 do mês atual
    let dataIniTemp = new Date(hoje);
    dataIniTemp.setDate(1);

    // se início > fim → recuar 1 mês
    if (dataIniTemp > dataFimTemp) {
      dataIniTemp.setMonth(dataIniTemp.getMonth() - 1);
      dataIniTemp.setDate(1);
    }

    return isoDate(dataIniTemp);
  });

  const [dataFim, setDataFim] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return isoDate(d);
  });

  // Combos (lista conjunta Motoristas/Ajudantes)
  const [pessoas, setPessoas] = useState([]);

  // Seleção (string ou null)
  const [pessoaSel, setPessoaSel] = useState(null);

  const pessoasOptions = useMemo(() => {
    // Distribuição: só pode ver o próprio nome
    if (!isAdminLike && distribuicaoNome) return [distribuicaoNome];
    return pessoas;
  }, [isAdminLike, distribuicaoNome, pessoas]);

  // Resultado agregado (linhas)
  const [linhas, setLinhas] = useState([]);
  const [divisorValorTon, setDivisorValorTon] = useState(0);
  const [acrescimoValorTon, setAcrescimoValorTon] = useState(0);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgLoading, setDlgLoading] = useState(false);
  const [dlgTitulo, setDlgTitulo] = useState("");
  const [dlgCols, setDlgCols] = useState([]);
  const [dlgRows, setDlgRows] = useState([]);
  const [dlgTotal, setDlgTotal] = useState(null);
  const [dlgTotalAccessor, setDlgTotalAccessor] = useState(null);

  const [dlgOvOpen, setDlgOvOpen] = useState(false);
  const [dlgOvLoading, setDlgOvLoading] = useState(false);
  const [dlgOvSaving, setDlgOvSaving] = useState(false);

  const [dlgOvHistOpen, setDlgOvHistOpen] = useState(false);
  const [dlgOvHistLoading, setDlgOvHistLoading] = useState(false);
  const [dlgOvHistRows, setDlgOvHistRows] = useState([]);
  const [dlgOvHistRefTransporte, setDlgOvHistRefTransporte] = useState("");
  const [dlgOvHistDataFim, setDlgOvHistDataFim] = useState("");

  const [dlgFichaOpen, setDlgFichaOpen] = useState(false);
  const [dlgFichaLoading, setDlgFichaLoading] = useState(false);
  const [dlgFichaRow, setDlgFichaRow] = useState(null);

  const [dlgMeritoOpen, setDlgMeritoOpen] = useState(false);
  const [dlgMeritoLoading, setDlgMeritoLoading] = useState(false);
  const [dlgMeritoTitulo, setDlgMeritoTitulo] = useState("");
  const [dlgMeritoRows, setDlgMeritoRows] = useState([]);

  const [ovRefTransporte, setOvRefTransporte] = useState("");
  const [ovDataFim, setOvDataFim] = useState("");

  const [ovCalc, setOvCalc] = useState({
    comEntregas: 0,
    comRecolhas: 0,
    semEntregas: 0,
    semRecolhas: 0,
  });

  const [ovForm, setOvForm] = useState({
    comEntregas: 0,
    comRecolhas: 0,
    semEntregas: 0,
    semRecolhas: 0,
    observacoes: "",
  });

  const [ovInicial, setOvInicial] = useState(null);

  const [ovExiste, setOvExiste] = useState(false);

  const [ovWarn, setOvWarn] = useState("");

  const fecharDlg = () => {
    setDlgOpen(false);
    setDlgTitulo("");
    setDlgCols([]);
    setDlgRows([]);
    setDlgTotal(null);
  };

  const carregarCombos = async () => {
    setLoading(true);

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelTransportesCombos", { idutilizador: userData?.id || 0 })
    );

    const payload = dados || {};
    setPessoas(Array.isArray(payload.pessoas) ? payload.pessoas : []);

    showAlert(meta?.message || "Combos carregados.", meta?.severity || "success");
    setLoading(false);
  };

  const atualizarAnalise = async () => {
    setLoading(true);

    const body = {
      idutilizador: userData?.id || 0,
      dataIni,
      dataFim,
      pessoa: pessoaSel || "",
    };

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelVendasTransportesEstatisticas", body)
    );

    console.log("dados", dados);
    // console.log("err", err);
    console.log("meta", meta);

    if (err) {
      showAlert(err, "error");
      setLoading(false);
      return;
    }

    const payload = dados || {};
    setLinhas(Array.isArray(payload.linhas) ? payload.linhas : []);
    setDivisorValorTon(Number(payload.divisorValorTon || 0));
    setAcrescimoValorTon(Number(payload.acrescimoValorTon || 0));

    showAlert(meta?.message || "OK", meta?.severity || "success");
    setLoading(false);
  };

  const abrirDetalhe = async ({ row, tipo, titulo, cols }) => {
    setDlgOpen(true);
    setDlgLoading(true);
    setDlgTitulo(titulo);
    setDlgCols(cols);
    // por defeito, o total vai para a última coluna do dialog
    setDlgTotalAccessor(cols?.length ? cols[cols.length - 1].accessor : null);

    setDlgRows([]);
    setDlgTotal(null);

    const body = {
      idutilizador: userData?.id || 0,
      datafim: onlyDate(row?.datafim),
      reftransporte: row?.reftransporte,
      pessoa: pessoaSel || "",
      tipo,
    };

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelVendasTransportesEstatisticasDetalhe", body)
    );

    // console.log("dados", dados);

    if (err) {
      showAlert(err, "error");
      setDlgLoading(false);
      return;
    }

    const payload = dados || {};
    setDlgRows(Array.isArray(payload.linhas) ? payload.linhas : []);
    setDlgTotal(payload.total ?? null);

    showAlert(meta?.message || "OK", meta?.severity || "success");
    setDlgLoading(false);
  };

  const abrirFichaTransporte = async (row) => {
    const ref = (row?.reftransporte || "").toString().trim();
    if (!ref) {
      showAlert("Ref. Transporte em falta.", "error");
      return;
    }

    setDlgFichaOpen(true);
    setDlgFichaLoading(true);
    setDlgFichaRow(null);

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelFichaTransportePorRef", {
        reftransporte: ref,
      })
    );

    if (err) {
      showAlert(err, "error");
      setDlgFichaLoading(false);
      setDlgFichaOpen(false);
      return;
    }

    setDlgFichaRow(dados || null);
    showAlert(meta?.message || "Ficha encontrada.", meta?.severity || "success");
    setDlgFichaLoading(false);
  };

  const abrirDialogMeritoPessoal = async (row) => {
    const idutilizador = Number(row?.idutilizador || 0);
    const pessoa = String(row?.pessoa || "").trim();

    if (!idutilizador) {
      showAlert("Não foi possível identificar o funcionário desta linha.", "error");
      return;
    }

    setDlgMeritoOpen(true);
    setDlgMeritoLoading(true);
    setDlgMeritoTitulo(`Caraterísticas — ${pessoa || "Funcionário"}`);
    setDlgMeritoRows([]);

    const [dados, err] = await handleApi(
      api.post("UtilizadoresAcessos/SelUtilizadorCaracteristicasItens", {
        idutilizador,
      })
    );

    if (err) {
      showAlert(err, "error");
      setDlgMeritoLoading(false);
      return;
    }

    setDlgMeritoRows(Array.isArray(dados) ? dados : []);
    setDlgMeritoLoading(false);
  };

  const abrirHistoricoOverride = async (row) => {
    const reftransporte = String(row?.reftransporte || "").trim();
    const datafim = onlyDate(row?.datafim);

    if (!reftransporte) {
      showAlert("Ref. Transporte em falta.", "error");
      return;
    }

    if (!datafim) {
      showAlert("Data do transporte em falta.", "error");
      return;
    }

    setDlgOvHistOpen(true);
    setDlgOvHistLoading(true);
    setDlgOvHistRows([]);
    setDlgOvHistRefTransporte(reftransporte);
    setDlgOvHistDataFim(datafim);

    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelFaturasVendasTransportesOverrideHistorico", {
        reftransporte,
        datafim,
      })
    );

    if (err) {
      showAlert(err, "error");
      setDlgOvHistLoading(false);
      return;
    }

    setDlgOvHistRows(Array.isArray(dados) ? dados : []);
    setDlgOvHistLoading(false);
  };

  const abrirOverride = async (row) => {
    const ref = row?.reftransporte || "";
    const data = onlyDate(row?.datafim);

    setDlgOvOpen(true);
    setDlgOvLoading(true);

    setOvRefTransporte(ref);
    setOvDataFim(data);

    const calc = {
      comEntregas: Number(row?.calc_qtd_clientes_com_entregas || 0),
      comRecolhas: Number(row?.calc_qtd_clientes_com_recolhas || 0),
      semEntregas: Number(row?.calc_qtd_clientes_sem_entregas || 0),
      semRecolhas: Number(row?.calc_qtd_clientes_sem_recolhas || 0),
    };

    setOvCalc(calc);

    // 1) tenta ler override real do Postgres (fonte de verdade)
    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelFaturasVendasTransportesOverride", {
        reftransporte: ref,
        datafim: data,
      })
    );

    // 2) Se existir override -> usar DB. Se não existir -> usar calculado.
    if (!err && dados) {
      setOvExiste(true);

      const formDb = {
        comEntregas: Number(dados?.qtd_clientes_com_entregas ?? calc.comEntregas),
        comRecolhas: Number(dados?.qtd_clientes_com_recolhas ?? calc.comRecolhas),
        semEntregas: Number(dados?.qtd_clientes_sem_entregas ?? calc.semEntregas),
        semRecolhas: Number(dados?.qtd_clientes_sem_recolhas ?? calc.semRecolhas),
        observacoes: String(dados?.observacoes || ""),
      };

      setOvForm(formDb);
      setOvInicial(formDb);
    } else {
      setOvExiste(false);

      const formCalc = {
        comEntregas: calc.comEntregas,
        comRecolhas: calc.comRecolhas,
        semEntregas: calc.semEntregas,
        semRecolhas: calc.semRecolhas,
        observacoes: "",
      };

      setOvForm(formCalc);
      setOvInicial(formCalc);
    }

    setDlgOvLoading(false);
  };

  const gravarOverride = async () => {
    setDlgOvSaving(true);

    const body = {
      reftransporte: ovRefTransporte,
      datafim: ovDataFim,

      idutilizador: userData?.id || 0,

      calc_qtd_clientes_com_entregas: Number(ovCalc.comEntregas || 0),
      calc_qtd_clientes_com_recolhas: Number(ovCalc.comRecolhas || 0),
      calc_qtd_clientes_sem_entregas: Number(ovCalc.semEntregas || 0),
      calc_qtd_clientes_sem_recolhas: Number(ovCalc.semRecolhas || 0),

      qtd_clientes_com_entregas: Number(ovForm.comEntregas || 0),
      qtd_clientes_com_recolhas: Number(ovForm.comRecolhas || 0),
      qtd_clientes_sem_entregas: Number(ovForm.semEntregas || 0),
      qtd_clientes_sem_recolhas: Number(ovForm.semRecolhas || 0),

      observacoes: ovForm.observacoes || "",
    };

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/UpdFaturasVendasTransportesOverride", body)
    );

    if (err) {
      showAlert(err, "error");
      setDlgOvSaving(false);
      return;
    }

    showAlert(meta?.message || "Override gravado.", meta?.severity || "success");

    // Recarregar o quadro para aplicar o override automaticamente
    await atualizarAnalise();

    setDlgOvSaving(false);
    setDlgOvOpen(false);
  };

  const totalEntregas = useMemo(() => {
    const n = (v) => Number(v || 0);
    return n(ovCalc.comEntregas) + n(ovCalc.semEntregas);
  }, [ovCalc]);

  const totalRecolhas = useMemo(() => {
    const n = (v) => Number(v || 0);
    return n(ovCalc.comRecolhas) + n(ovCalc.semRecolhas);
  }, [ovCalc]);

  const toIntOr0 = (v) => {
    const x = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(x) ? x : 0;
  };

  const aplicarComplementoEntregasCom = () => {
    setOvWarn("");
    const novoCom = toIntOr0(ovForm.comEntregas);
    const novoSem = totalEntregas - novoCom;

    if (novoSem < 0) {
      setOvWarn(`Não é possível aumentar "Clientes c/ Entregas" acima de ${totalEntregas}.`);
      return;
    }

    setOvForm((p) => ({
      ...p,
      comEntregas: String(novoCom),
      semEntregas: String(novoSem),
    }));
  };

  const aplicarComplementoEntregasSem = () => {
    setOvWarn("");
    const novoSem = toIntOr0(ovForm.semEntregas);
    const novoCom = totalEntregas - novoSem;

    if (novoCom < 0) {
      setOvWarn(`Não é possível aumentar "Clientes s/ Entregas" acima de ${totalEntregas}.`);
      return;
    }

    setOvForm((p) => ({
      ...p,
      semEntregas: String(novoSem),
      comEntregas: String(novoCom),
    }));
  };

  const aplicarComplementoRecolhasCom = () => {
    setOvWarn("");
    const novoCom = toIntOr0(ovForm.comRecolhas);
    const novoSem = totalRecolhas - novoCom;

    if (novoSem < 0) {
      setOvWarn(`Não é possível aumentar "Clientes c/ Recolhas" acima de ${totalRecolhas}.`);
      return;
    }

    setOvForm((p) => ({
      ...p,
      comRecolhas: String(novoCom),
      semRecolhas: String(novoSem),
    }));
  };

  const aplicarComplementoRecolhasSem = () => {
    setOvWarn("");
    const novoSem = toIntOr0(ovForm.semRecolhas);
    const novoCom = totalRecolhas - novoSem;

    if (novoCom < 0) {
      setOvWarn(`Não é possível aumentar "Clientes s/ Recolhas" acima de ${totalRecolhas}.`);
      return;
    }

    setOvForm((p) => ({
      ...p,
      semRecolhas: String(novoSem),
      comRecolhas: String(novoCom),
    }));
  };

  const ovDirty = useMemo(() => {
    if (!ovInicial) return false;

    const n = (v) => Number(v || 0);
    const s = (v) => String(v || "").trim();

    return (
      n(ovForm.comEntregas) !== n(ovInicial.comEntregas) ||
      n(ovForm.comRecolhas) !== n(ovInicial.comRecolhas) ||
      n(ovForm.semEntregas) !== n(ovInicial.semEntregas) ||
      n(ovForm.semRecolhas) !== n(ovInicial.semRecolhas) ||
      s(ovForm.observacoes) !== s(ovInicial.observacoes)
    );
  }, [ovForm, ovInicial]);

  useEffect(() => {
    (async () => {
      await carregarCombos();
      // não dispara automaticamente a análise (só via botão),
      // para manter o comportamento igual ao que pediste
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------- Agrupamentos: pessoa > datafim -------------------
  const gruposPessoa = useMemo(() => {
    const m = groupBy(linhas, (r) => String(r?.pessoa ?? ""));
    return m;
  }, [linhas]);

  // ------------------- Tabela (por dia, linhas por transporte) -------------------
  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "_acao_override",
        disableSortBy: true,
        Cell: ({ row }) => {
          const estadoFicha = row?.original?.estado_ficha;

          let corFicha = "info";
          let tituloFicha = "Ver detalhe da ficha de transporte";

          if (estadoFicha === null || estadoFicha === undefined) {
            corFicha = "error";
            tituloFicha = "Ainda não existe ficha de transporte";
          } else if (Number(estadoFicha) !== 1) {
            corFicha = "warning";
            tituloFicha = "Ficha de transporte existente, mas ainda não encerrada";
          }

          return (
            <CellCenter>
              <IconButton
                size="small"
                title={tituloFicha}
                onClick={(e) => {
                  e.stopPropagation();
                  abrirFichaTransporte(row?.original);
                }}
              >
                <DescriptionIcon color={corFicha} fontSize="inherit" />
              </IconButton>

              {isAdminLike && (
                <>
                  <IconButton
                    size="small"
                    title="Editar override (clientes)"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirOverride(row?.original);
                    }}
                  >
                    <EditIcon color="error" fontSize="inherit" />
                  </IconButton>

                  {Number(row?.original?.override_id || 0) > 0 && (
                    <IconButton
                      size="small"
                      title="Histórico de alterações manuais"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirHistoricoOverride(row?.original);
                      }}
                    >
                      <HistoryIcon color="info" fontSize="inherit" />
                    </IconButton>
                  )}
                </>
              )}
            </CellCenter>
          );
        },
      },

      {
        Header: "Transporte",
        accessor: "reftransporte",
        Cell: ({ value, row }) => {
          const ref = value;
          const data = onlyDate(row?.original?.datafim);

          if (!ref) return null;

          return (
            <span
              role="button"
              tabIndex={0}
              title="Abrir Transportes (novo separador)"
              onClick={(e) => {
                e.stopPropagation();
                abrirTransportesNovoSeparador(ref, data);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  abrirTransportesNovoSeparador(ref, data);
                }
              }}
              style={{
                color: "#1a73e8",
                textDecoration: "underline",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {ref}
            </span>
          );
        },
      },

      {
        Header: "Kgs Entregues",
        accessor: "peso_entregue",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtNum(value, 3)}</span>
            {Number(value || 0) > 0 && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "PESO_ENTREGUE",
                    titulo: `Peso Entregue — ${onlyDate(row.original.datafim)} — Transporte ${
                      row.original.reftransporte
                    }`,
                    cols: [
                      { Header: "Cliente", accessor: "nomecliente" },
                      { Header: "Fatura", accessor: "faturanumero" },
                      { Header: "Peso Entregue", accessor: "pesobrutoentregue" },
                    ],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Kgs Recolhidos",
        accessor: "peso_recolhido",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtNum(value, 3)}</span>
            {Number(value || 0) > 0 && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "PESO_RECOLHIDO",
                    titulo: `Peso Recolhido — ${onlyDate(row.original.datafim)} — Transporte ${
                      row.original.reftransporte
                    }`,
                    cols: [
                      { Header: "Cliente", accessor: "nomecliente" },
                      { Header: "Fatura", accessor: "faturanumero" },
                      { Header: "Peso Recolhido", accessor: "pesodevolucao" },
                    ],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Clientes c/ Entregas",
        accessor: "qtd_clientes_com_entregas",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtInt(value)}</span>
            {(Number(value || 0) > 0 ||
              Number(row?.original?.calc_qtd_clientes_com_entregas || 0) > 0) && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "CLIENTES_COM_ENTREGAS",
                    titulo: `Clientes c/ Entregas — ${onlyDate(
                      row.original.datafim
                    )} — Transporte ${row.original.reftransporte}`,
                    cols: [{ Header: "Cliente", accessor: "nomecliente" }],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Clientes c/ Recolhas",
        accessor: "qtd_clientes_com_recolhas",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtInt(value)}</span>
            {(Number(value || 0) > 0 ||
              Number(row?.original?.calc_qtd_clientes_com_recolhas || 0) > 0) && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "CLIENTES_COM_RECOLHAS",
                    titulo: `Clientes c/ Recolhas — ${onlyDate(
                      row.original.datafim
                    )} — Transporte ${row.original.reftransporte}`,
                    cols: [{ Header: "Cliente", accessor: "nomecliente" }],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Clientes s/ Entregas",
        accessor: "qtd_clientes_sem_entregas",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtInt(value)}</span>
            {(Number(value || 0) > 0 ||
              Number(row?.original?.calc_qtd_clientes_sem_entregas || 0) > 0) && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "CLIENTES_SEM_ENTREGAS",
                    titulo: `Clientes s/ Entregas — ${onlyDate(
                      row.original.datafim
                    )} — Transporte ${row.original.reftransporte}`,
                    cols: [
                      { Header: "Cliente", accessor: "nomecliente" },
                      { Header: "Fatura", accessor: "faturanumero" },
                    ],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Clientes s/ Recolhas",
        accessor: "qtd_clientes_sem_recolhas",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtInt(value)}</span>
            {(Number(value || 0) > 0 ||
              Number(row?.original?.calc_qtd_clientes_sem_recolhas || 0) > 0) && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "CLIENTES_SEM_RECOLHAS",
                    titulo: `Clientes s/ Recolhas — ${onlyDate(
                      row.original.datafim
                    )} — Transporte ${row.original.reftransporte}`,
                    cols: [
                      { Header: "Cliente", accessor: "nomecliente" },
                      { Header: "Motivo", accessor: "motivodescricao" },
                    ],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Clientes c/ Vasilhame",
        accessor: "qtd_clientes_com_vasilhame",
        Cell: ({ value, row }) => (
          <CellCenter>
            <span>{fmtInt(value)}</span>
            {Number(value || 0) > 0 && (
              <IconButton
                size="small"
                color="info"
                title="Detalhe"
                onClick={() =>
                  abrirDetalhe({
                    row: row.original,
                    tipo: "CLIENTES_COM_VASILHAME",
                    titulo: `Clientes c/ Vasilhame — ${onlyDate(
                      row.original.datafim
                    )} — Transporte ${row.original.reftransporte}`,
                    cols: [
                      { Header: "Cliente", accessor: "nomecliente" },
                      { Header: "Fatura", accessor: "faturanumero" },
                    ],
                  })
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </CellCenter>
        ),
      },

      {
        Header: "Valor / Ton (pts)",
        accessor: "valor_por_ton_outros",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtMoney(value)}</CellCenter>,
      },
      {
        Header: "Eficiência (%)",
        accessor: "taxa_eficiencia_outros_pct",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtNum(value, 1)} %</CellCenter>,
      },
      {
        Header: "Valor Eficiência (pts)",
        accessor: "valor_eficiencia_outros",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtMoney(value)}</CellCenter>,
      },
      {
        Header: "Limpeza Viatura",
        accessor: "limpeza_viatura_pts",
        disableSortBy: true,
        Cell: ({ row }) => {
          const pts = Number(row.original?.limpeza_viatura_pts || 0);
          const pct = Number(row.original?.limpeza_viatura_pct || 0);
          return (
            <CellCenter>
              <span style={{ color: pct === 0 ? "#c62828" : undefined }}>
                {fmtMoney(pts)} pts (+ {fmtNum(pct, 0)} %)
              </span>
            </CellCenter>
          );
        },
      },
      {
        Header: "Quebras/Danos",
        accessor: "quebras_danos_pts",
        disableSortBy: true,
        Cell: ({ row }) => {
          const pts = Number(row.original?.quebras_danos_pts || 0);
          const pct = Number(row.original?.quebras_danos_pct || 0);
          return (
            <CellCenter>
              <span style={{ color: pct === 0 ? "#c62828" : undefined }}>
                {fmtMoney(pts)} pts (+ {fmtNum(pct, 0)} %)
              </span>
            </CellCenter>
          );
        },
      },
      {
        Header: "Ajuda Armazém",
        accessor: "ajuda_armazem_pts",
        disableSortBy: true,
        Cell: ({ row }) => {
          const pts = Number(row.original?.ajuda_armazem_pts || 0);
          const pct = Number(row.original?.ajuda_armazem_pct || 0);
          return (
            <CellCenter>
              <span style={{ color: pct === 0 ? "#c62828" : undefined }}>
                {fmtMoney(pts)} pts (+ {fmtNum(pct, 0)} %)
              </span>
            </CellCenter>
          );
        },
      },
      {
        Header: "Fator Mérito Pessoal",
        accessor: "fator_merito_pessoal_pts",
        disableSortBy: true,
        Cell: ({ row }) => {
          const pts = Number(row.original?.fator_merito_pessoal_pts || 0);
          const pct = Number(row.original?.fator_merito_pessoal_pct || 0);
          const fator = Number(row.original?.fator_merito_pessoal || 0);

          return (
            <CellCenter>
              <span style={{ color: pct === 0 ? "#c62828" : undefined }}>
                {fmtMoney(pts)} pts (+ {fmtNum(pct, 0)} % | fator {fmtNum(fator, 2)})
              </span>
            </CellCenter>
          );
        },
      },
      {
        Header: "Total Eficiência (pts)",
        accessor: "total_eficiencia_pts",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtMoney(value)}</CellCenter>,
      },

      {
        Header: "Ton. Outros",
        accessor: "toneladas_outros",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtNum(value, 3)}</CellCenter>,
      },
      {
        Header: "Total Outros (pts)",
        accessor: "total_receber_outros",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtMoney(value)}</CellCenter>,
      },

      {
        Header: "Pts/Ton Facilitada",
        accessor: "valor_eficiencia_facilitada",
        disableSortBy: true,
        Cell: () => <CellCenter></CellCenter>,
      },
      {
        Header: "Ton. Facilitada",
        accessor: "toneladas_facilitada",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtNum(value, 3)}</CellCenter>,
      },

      {
        Header: "Total Facilitada (pts)",
        accessor: "total_receber_facilitada",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtMoney(value)}</CellCenter>,
      },

      {
        Header: "Total a Receber (pts)",
        accessor: "total_receber",
        disableSortBy: true,
        Cell: ({ value }) => <CellCenter>{fmtMoney(value)}</CellCenter>,
      },
    ],
    [abrirDetalhe, isAdminLike]
  );

  function TabelaDia({ data }) {
    const dataDia = Array.isArray(data) ? data : [];
    const primeiraLinhaDia = dataDia.length > 0 ? dataDia[0] : null;

    const table = useTable(
      {
        columns,
        data: dataDia,
        autoResetSortBy: false,
      },
      useSortBy
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

    const totaisDia = (Array.isArray(data) ? data : []).reduce(
      (acc, r) => {
        const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v) || 0);

        acc.peso_entregue += num(r?.peso_entregue);
        acc.peso_recolhido += num(r?.peso_recolhido);

        acc.peso_entregue_facilitada += num(r?.peso_entregue_facilitada);
        acc.peso_recolhido_facilitada += num(r?.peso_recolhido_facilitada);
        acc.peso_entregue_outros += num(r?.peso_entregue_outros);
        acc.peso_recolhido_outros += num(r?.peso_recolhido_outros);

        acc.qtd_clientes_com_entregas += num(r?.qtd_clientes_com_entregas);
        acc.qtd_clientes_com_recolhas += num(r?.qtd_clientes_com_recolhas);
        acc.qtd_clientes_sem_entregas += num(r?.qtd_clientes_sem_entregas);
        acc.qtd_clientes_sem_recolhas += num(r?.qtd_clientes_sem_recolhas);
        acc.qtd_clientes_com_vasilhame += num(r?.qtd_clientes_com_vasilhame);

        acc.toneladas_facilitada += num(r?.toneladas_facilitada);
        acc.toneladas_outros += num(r?.toneladas_outros);

        acc.valor_por_ton_outros += num(r?.valor_por_ton_outros);
        acc.taxa_eficiencia_outros_pct += num(r?.taxa_eficiencia_outros_pct);
        acc.valor_eficiencia_outros += num(r?.valor_eficiencia_outros);

        acc.limpeza_viatura_pts += num(r?.limpeza_viatura_pts);
        acc.quebras_danos_pts += num(r?.quebras_danos_pts);
        acc.ajuda_armazem_pts += num(r?.ajuda_armazem_pts);
        acc.fator_merito_pessoal_pts += num(r?.fator_merito_pessoal_pts);
        acc.total_eficiencia_pts += num(r?.total_eficiencia_pts);

        acc.total_receber_facilitada += num(r?.total_receber_facilitada);
        acc.total_receber_outros += num(r?.total_receber_outros);
        acc.total_receber += num(r?.total_receber);

        return acc;
      },
      {
        peso_entregue: 0,
        peso_recolhido: 0,

        peso_entregue_facilitada: 0,
        peso_recolhido_facilitada: 0,
        peso_entregue_outros: 0,
        peso_recolhido_outros: 0,

        qtd_clientes_com_entregas: 0,
        qtd_clientes_com_recolhas: 0,
        qtd_clientes_sem_entregas: 0,
        qtd_clientes_sem_recolhas: 0,
        qtd_clientes_com_vasilhame: 0,

        toneladas_facilitada: 0,
        toneladas_outros: 0,

        valor_por_ton_outros: 0,
        taxa_eficiencia_outros_pct: 0,
        valor_eficiencia_outros: 0,

        limpeza_viatura_pts: 0,
        quebras_danos_pts: 0,
        ajuda_armazem_pts: 0,
        fator_merito_pessoal_pts: 0,
        total_eficiencia_pts: 0,

        total_receber_facilitada: 0,
        total_receber_outros: 0,
        total_receber: 0,
      }
    );

    // ===== CÁLCULOS DIÁRIOS =====

    const pesoTotal = totaisDia.peso_entregue + totaisDia.peso_recolhido;
    const toneladas = totaisDia.toneladas_facilitada + totaisDia.toneladas_outros;

    const totalClientesEf =
      totaisDia.qtd_clientes_com_entregas + totaisDia.qtd_clientes_sem_entregas;

    const taxaEficienciaPct = totaisDia.taxa_eficiencia_outros_pct || 0;
    const valorPorTonelada = totaisDia.valor_por_ton_outros || 0;
    const valorEficienciaDiaria = totaisDia.valor_eficiencia_outros || 0;
    const totalReceberOutrosDia = totaisDia.total_receber_outros || 0;
    const totalReceberDia = totaisDia.total_receber || 0;

    // ===== Dialog de cálculo (footer) =====
    const [calcOpen, setCalcOpen] = useState(false);
    const [calcAcc, setCalcAcc] = useState("");

    const abrirCalc = (acc) => {
      setCalcAcc(acc || "");
      setCalcOpen(true);
    };

    const fecharCalc = () => {
      setCalcOpen(false);
      setCalcAcc("");
    };

    const calcTituloMap = {
      valor_por_ton_outros: "Cálculo — Valor / Ton (pts)",
      taxa_eficiencia_outros_pct: "Cálculo — Eficiência (%)",
      valor_eficiencia_outros: "Cálculo — Valor Eficiência (pts)",
      total_eficiencia_pts: "Cálculo — Total Eficiência (pts)",
      total_receber: "Cálculo — Total a Receber (pts)",
    };

    const calcTitulo = calcTituloMap[calcAcc] || "Cálculo";

    let regraEficiencia = "Conforme escalões configurados no backend.";

    const totaisDiaFull = {
      ...(totaisDia || {}),
      valor_por_ton_outros: valorPorTonelada,
      taxa_eficiencia_outros_pct: taxaEficienciaPct,
      valor_eficiencia_outros: valorEficienciaDiaria,
      total_eficiencia_pts: totaisDia.total_eficiencia_pts || 0,
      total_receber_outros: totalReceberOutrosDia,
      total_receber: totalReceberDia,
    };

    return (
      <div style={{ width: "100%", overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
        <table
          {...getTableProps()}
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            {headerGroups.map((hg) => (
              <tr key={hg.getHeaderGroupProps().key} {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <th
                    key={col.getHeaderProps().key}
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#fff",
                      zIndex: 1,
                      textAlign: "center",
                      padding: "8px 10px",
                      borderBottom: "1px solid rgba(0,0,0,0.15)",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                    title="Ordenar"
                  >
                    {col.render("Header")}
                    <span style={{ marginLeft: 6, opacity: 0.6 }}>
                      {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr key={row.getRowProps().key} {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td
                      key={cell.getCellProps().key}
                      {...cell.getCellProps()}
                      style={{
                        padding: "8px 10px",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}

            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ padding: 12, color: "#667", fontSize: 12, opacity: 0.85 }}
                >
                  Sem linhas para este dia.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              {columns.map((c, idx) => {
                // 1ª coluna: texto "Total do dia"
                if (idx === 0) {
                  return (
                    <td
                      key={c.accessor || c.id || idx}
                      style={{
                        padding: "10px",
                        borderTop: "2px solid rgba(0,0,0,0.12)",
                        fontWeight: 700,
                        background: "#fff",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                      }}
                    >
                      Total do dia
                    </td>
                  );
                }

                const acc = c.accessor;

                const v = acc === "valor_eficiencia_facilitada" ? 0.5 : totaisDiaFull?.[acc];

                // se não for coluna numérica (ex: ações), deixa vazio
                if (v === undefined) {
                  return (
                    <td
                      key={c.accessor || c.id || idx}
                      style={{
                        padding: "10px",
                        borderTop: "2px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        textAlign: "center",
                      }}
                    />
                  );
                }

                const isPeso =
                  acc === "peso_entregue" ||
                  acc === "peso_recolhido" ||
                  acc === "toneladas_facilitada" ||
                  acc === "toneladas_outros";

                const isPct = acc === "taxa_eficiencia_outros_pct";

                const isMoney =
                  acc === "valor_eficiencia_facilitada" ||
                  acc === "valor_por_ton_outros" ||
                  acc === "valor_eficiencia_outros" ||
                  acc === "limpeza_viatura_pts" ||
                  acc === "quebras_danos_pts" ||
                  acc === "ajuda_armazem_pts" ||
                  acc === "fator_merito_pessoal_pts" ||
                  acc === "total_eficiencia_pts" ||
                  acc === "total_receber_facilitada" ||
                  acc === "total_receber_outros" ||
                  acc === "total_receber";

                let texto = "";
                if (isPeso) texto = fmtNum(v);
                else if (isPct) texto = `${fmtNum(v, 1)} %`;
                else if (isMoney) texto = `${fmtMoney(v)} pts`;
                else texto = fmtInt(v);

                return (
                  <td
                    key={c.accessor || c.id || idx}
                    style={{
                      padding: "10px",
                      borderTop: "2px solid rgba(0,0,0,0.12)",
                      fontWeight: 700,
                      background: "#fff",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        width: "100%",
                      }}
                    >
                      <span
                        style={
                          acc === "taxa_eficiencia_outros_pct"
                            ? { color: getCorEficiencia(v), fontWeight: 700 }
                            : acc === "total_receber"
                            ? { color: "#1a73e8", fontWeight: 800 }
                            : acc === "total_receber_outros" || acc === "total_receber_facilitada"
                            ? { color: "#1a73e8", fontWeight: 700 }
                            : undefined
                        }
                      >
                        {texto}
                      </span>

                      {acc === "fator_merito_pessoal_pts" && (
                        <IconButton
                          size="small"
                          color="info"
                          title="Ver caraterísticas do funcionário"
                          onClick={() => {
                            if (primeiraLinhaDia) {
                              abrirDialogMeritoPessoal(primeiraLinhaDia);
                            }
                          }}
                          style={{ padding: 2, color: "#1976d2" }}
                        >
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      )}

                      {acc === "total_receber" && (
                        <IconButton
                          size="small"
                          title="Ver cálculo"
                          color="info"
                          onClick={() => abrirCalc(acc)}
                          style={{ padding: 2 }}
                        >
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
        <Dialog
          open={calcOpen}
          onClose={fecharCalc}
          fullWidth
          maxWidth="sm"
          PaperComponent={PaperComponent}
        >
          <DialogTitle id="draggable-dialog-title" sx={{ cursor: "move", fontWeight: 800 }}>
            {calcTitulo}
          </DialogTitle>

          <DialogContent dividers>
            <MDBox>
              <MDTypography variant="caption" display="block">
                Peso Entregue (kg): {fmtNum(totaisDia.peso_entregue, 3)}
              </MDTypography>
              <MDTypography variant="caption" display="block">
                Peso Recolhido (kg): {fmtNum(totaisDia.peso_recolhido, 3)}
              </MDTypography>
              <MDTypography variant="caption" display="block">
                Peso Total (kg): {fmtNum(pesoTotal, 3)}
              </MDTypography>
              <MDTypography variant="caption" display="block">
                Toneladas: {fmtNum(toneladas, 3)}
              </MDTypography>

              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Valor / Ton (pts)
                </MDTypography>

                <MDTypography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                  Peso Total (kg) = Peso Entregue (kg) + Peso Recolhido (kg)
                </MDTypography>
                <MDTypography variant="caption" display="block">
                  = {fmtNum(totaisDia.peso_entregue, 3)} + {fmtNum(totaisDia.peso_recolhido, 3)}
                </MDTypography>
                <MDTypography variant="caption" display="block" fontWeight="bold">
                  = {fmtNum(pesoTotal, 3)} kg
                </MDTypography>

                <MDBox mt={1}>
                  <MDTypography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                    Fórmula: ((Peso Total / 1000) / {fmtNum(divisorValorTon, 1)}) +{" "}
                    {fmtNum(acrescimoValorTon, 1)}
                  </MDTypography>
                  <MDTypography variant="caption" display="block">
                    = (({fmtNum(pesoTotal, 3)} / 1000) / {fmtNum(divisorValorTon, 1)}) +{" "}
                    {fmtNum(acrescimoValorTon, 1)}
                  </MDTypography>
                  <MDTypography variant="caption" display="block">
                    = (({fmtNum(toneladas, 3)} / {fmtNum(divisorValorTon, 1)}) +{" "}
                    {fmtNum(acrescimoValorTon, 1)})
                  </MDTypography>
                  <MDTypography variant="caption" display="block" fontWeight="bold">
                    = {fmtMoney(valorPorTonelada)} pts / ton
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Eficiência (%)
                </MDTypography>
                <MDTypography variant="caption" display="block">
                  Fórmula: Clientes c/ Entregas / (Clientes c/ Entregas + Clientes s/ Entregas)
                </MDTypography>
                <MDTypography variant="caption" display="block">
                  = {fmtInt(totaisDia.qtd_clientes_com_entregas)} / (
                  {fmtInt(totaisDia.qtd_clientes_com_entregas)} +{" "}
                  {fmtInt(totaisDia.qtd_clientes_sem_entregas)})
                </MDTypography>
                <MDTypography variant="caption" display="block" fontWeight="bold">
                  = {fmtNum(taxaEficienciaPct, 1)} %
                </MDTypography>
              </MDBox>

              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Valor Eficiência (pts)
                </MDTypography>
                <MDTypography variant="caption" display="block">
                  {regraEficiencia}
                </MDTypography>
                <MDTypography variant="caption" display="block" fontWeight="bold">
                  = {fmtMoney(valorEficienciaDiaria)} pts / ton
                </MDTypography>
              </MDBox>

              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Total a Receber (pts)
                </MDTypography>

                <MDTypography variant="caption" display="block">
                  Total Facilitada = Ton. Facilitada × 0.50
                </MDTypography>
                <MDTypography variant="caption" display="block">
                  = {fmtNum(totaisDia.toneladas_facilitada, 3)} × 0.50
                </MDTypography>
                <MDTypography variant="caption" display="block" fontWeight="bold">
                  = {fmtMoney(totaisDia.total_receber_facilitada)} pts
                </MDTypography>

                <MDBox mt={1}>
                  <MDTypography variant="caption" display="block">
                    Total Outros = Ton. Outros × Valor Eficiência
                  </MDTypography>
                  <MDTypography variant="caption" display="block">
                    = {fmtNum(totaisDia.toneladas_outros, 3)} × {fmtMoney(valorEficienciaDiaria)}
                  </MDTypography>
                  <MDTypography variant="caption" display="block" fontWeight="bold">
                    = {fmtMoney(totalReceberOutrosDia)} pts
                  </MDTypography>
                </MDBox>

                <MDBox mt={1}>
                  <MDTypography variant="caption" display="block">
                    Total a Receber = Total Facilitada + Total Outros
                  </MDTypography>
                  <MDTypography variant="caption" display="block">
                    = {fmtMoney(totaisDia.total_receber_facilitada)} +{" "}
                    {fmtMoney(totalReceberOutrosDia)}
                  </MDTypography>
                  <MDTypography variant="caption" display="block" fontWeight="bold">
                    = {fmtMoney(totalReceberDia)} pts
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </DialogContent>

          <DialogActions>
            <MDButton onClick={fecharCalc} color="secondary">
              Fechar
            </MDButton>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  if (!podeVerPagina) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <PageHeader />
        <MDBox pt={2} pb={3}>
          <Card>
            <MDBox p={2}>
              <MDTypography style={{ fontSize: 14, fontWeight: 700 }}>
                Sem permissões para consultar estas estatísticas.
              </MDTypography>
              <MDTypography style={{ fontSize: 12, marginTop: 8, opacity: 0.85 }}>
                Esta página não está disponível para o seu perfil de utilizador.
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  // --- UI --------------------------------------------------------------------
  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* PROGRESS GLOBAL — sempre aqui, logo após o navbar */}
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={2} pb={3}>
        <Card>
          <MDBox p={2}>
            <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
              Escolhe o intervalo de datas e o Distribuidor. Depois clica em “Atualizar Análise”.
            </MDTypography>

            <MDBox mt={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Data início</div>
                <TextField
                  type="date"
                  value={dataIni}
                  onChange={(e) => setDataIni(e.target.value)}
                  size="small"
                  fullWidth
                />
              </div>

              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Data fim</div>
                <TextField
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  size="small"
                  fullWidth
                />
              </div>

              <MDBox
                sx={{
                  minWidth: { xs: 0, sm: 260 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <div style={{ fontSize: 12, marginBottom: 6 }}>Distribuidor</div>
                <Autocomplete
                  options={pessoasOptions}
                  filterOptions={createAutocompleteFilter((opt) => opt || "")}
                  value={pessoaSel}
                  onChange={(e, v) => setPessoaSel(v)}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
              </MDBox>

              <MDButton color="info" onClick={atualizarAnalise} disabled={!pessoaSel}>
                Atualizar Análise
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        <MDBox mt={2}>
          {Array.from(gruposPessoa.entries()).map(([pessoa, rowsPessoa]) => {
            const porDia = groupBy(rowsPessoa, (r) => onlyDate(r?.datafim));
            const dias = Array.from(porDia.keys()).sort((a, b) => (a > b ? -1 : 1)); // desc

            const totalReceberPeriodoPessoa = Array.from(porDia.values()).reduce(
              (acc, linhasDia) => {
                const totalDia = (Array.isArray(linhasDia) ? linhasDia : []).reduce((soma, r) => {
                  return soma + Number(r?.total_receber || 0);
                }, 0);

                return acc + totalDia;
              },
              0
            );

            return (
              <Card key={pessoa || "sem-pessoa"} style={{ marginBottom: 16 }}>
                <MDBox p={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDTypography variant="h6" style={{ fontSize: 14 }}>
                      {pessoa || "(sem pessoa)"}
                    </MDTypography>

                    <MDTypography
                      variant="button"
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: "#1a237e",
                        textAlign: "right",
                      }}
                    >
                      Total do Período:&nbsp;
                      <span style={{ color: "#2f3ddb" }}>
                        {fmtMoney(totalReceberPeriodoPessoa)} pts
                      </span>
                    </MDTypography>
                  </MDBox>

                  <MDBox mt={2}>
                    {dias.map((dia) => (
                      <MDBox key={dia} mb={2}>
                        <MDTypography style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                          {dia}
                        </MDTypography>
                        <TabelaDia data={porDia.get(dia)} />
                      </MDBox>
                    ))}

                    {dias.length === 0 && (
                      <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
                        Sem dados para o intervalo selecionado.
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox
                    mt={2}
                    p={2}
                    display="flex"
                    justifyContent="flex-end"
                    sx={{
                      borderTop: "3px solid rgba(0,0,0,0.15)",
                      fontSize: 14,
                      fontWeight: 800,
                    }}
                  >
                    Total do Período:&nbsp;
                    <span style={{ color: "#2f3ddb" }}>
                      {fmtMoney(totalReceberPeriodoPessoa)} pts
                    </span>
                  </MDBox>
                </MDBox>
              </Card>
            );
          })}

          {Array.from(gruposPessoa.keys()).length === 0 && (
            <Card>
              <MDBox p={2}>
                <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
                  Ainda sem resultados. Seleciona Distribuidor e clica em “Atualizar Análise”.
                </MDTypography>
              </MDBox>
            </Card>
          )}
        </MDBox>
      </MDBox>

      <Dialog
        open={dlgOpen}
        onClose={fecharDlg}
        fullWidth
        maxWidth="md"
        PaperComponent={PaperComponent}
      >
        <DialogTitle
          id="draggable-dialog-title"
          sx={{
            cursor: "move",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {(() => {
            // tenta extrair "Transporte <ref>" do dlgTitulo
            const m = String(dlgTitulo || "").match(/Transporte\s+(\d+)/i);
            const ref = m?.[1] || null;

            if (!ref) return dlgTitulo;

            // tenta extrair a data "YYYY-MM-DD" do dlgTitulo
            const d = String(dlgTitulo || "").match(/(\d{4}-\d{2}-\d{2})/);
            const data = d?.[1] || null;

            const href = `/distribuicao/transportes?reftransporte=${encodeURIComponent(ref)}`;

            // devolve o título, mas com a ref clicável
            const partes = String(dlgTitulo).split(ref);

            return (
              <span>
                {partes[0]}
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "#1a73e8",
                    textDecoration: "underline",
                    fontWeight: 700,
                  }}
                  title="Abrir Transportes (novo separador)"
                >
                  {ref}
                </a>
                {partes.slice(1).join(ref)}
              </span>
            );
          })()}
          <IconButton size="small" onClick={fecharDlg} sx={{ ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {dlgLoading && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          {!dlgLoading && (
            <>
              {/* tabela simples (sem filtros) */}
              <div
                style={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "60vh",
                  position: "relative",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {dlgCols.map((c) => (
                        <th
                          key={c.accessor}
                          style={{
                            textAlign: "left",
                            padding: "8px 10px",
                            borderBottom: "1px solid rgba(0,0,0,0.15)",
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            background: "#fff",
                            zIndex: 2,
                          }}
                        >
                          {c.Header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {dlgRows.map((r, idx) => (
                      <tr key={idx}>
                        {dlgCols.map((c) => (
                          <td
                            key={c.accessor}
                            style={{
                              padding: "8px 10px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {String(r?.[c.accessor] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {dlgRows.length === 0 && (
                      <tr>
                        <td colSpan={dlgCols.length} style={{ padding: 12, opacity: 0.8 }}>
                          Sem registos.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {/* TOTAL: fica por baixo da coluna "numérica" (por defeito, a última) */}
                  {dlgTotal !== null && dlgCols.length > 0 && (
                    <tfoot>
                      <tr>
                        {(() => {
                          const foundIndex = dlgCols.findIndex(
                            (c) => c.accessor === dlgTotalAccessor
                          );
                          const idxTotal = foundIndex >= 0 ? foundIndex : dlgCols.length - 1;

                          return dlgCols.map((c, i) => {
                            // nas colunas antes do total: células vazias
                            if (i !== idxTotal) {
                              return (
                                <td
                                  key={c.accessor}
                                  style={{
                                    padding: "10px",
                                    borderTop: "2px solid rgba(0,0,0,0.15)",
                                    background: "#fff",
                                  }}
                                />
                              );
                            }

                            // coluna do total: "Total:" + valor na MESMA coluna (fica exatamente por baixo)
                            return (
                              <td
                                key={c.accessor}
                                style={{
                                  padding: "10px",
                                  borderTop: "2px solid rgba(0,0,0,0.15)",
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  background: "#fff",
                                }}
                              >
                                <span style={{ fontWeight: 700 }}>{String(dlgTotal)}</span>
                              </td>
                            );
                          });
                        })()}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={fecharDlg}>Fechar</MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dlgOvOpen}
        onClose={() => setDlgOvOpen(false)}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title-override"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          style={{
            cursor: "move",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          id="draggable-dialog-title-override"
        >
          <span>
            Override — Clientes — {ovDataFim} — Transporte {ovRefTransporte}
          </span>
          <IconButton onClick={() => setDlgOvOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {dlgOvLoading && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={22} />
            </MDBox>
          )}

          {!dlgOvLoading && (
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
                Por defeito são apresentados os valores **calculados** pela app.
                {ovExiste ? " Já existe override gravado." : " Ainda não existe override gravado."}
              </MDTypography>

              {ovWarn ? (
                <MDTypography style={{ fontSize: 12, color: "#b71c1c", marginTop: 8 }}>
                  {ovWarn}
                </MDTypography>
              ) : null}

              <MDBox display="flex" gap={2} flexWrap="wrap">
                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    Clientes c/ Entregas (calculado: {ovCalc.comEntregas})
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    value={ovForm.comEntregas}
                    onChange={(e) => {
                      setOvWarn("");
                      setOvForm((p) => ({ ...p, comEntregas: e.target.value }));
                    }}
                    onBlur={aplicarComplementoEntregasCom}
                  />
                </div>

                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    Clientes c/ Recolhas (calculado: {ovCalc.comRecolhas})
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    value={ovForm.comRecolhas}
                    onChange={(e) => {
                      setOvWarn("");
                      setOvForm((p) => ({ ...p, comRecolhas: e.target.value }));
                    }}
                    onBlur={aplicarComplementoRecolhasCom}
                  />
                </div>

                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    Clientes s/ Entregas (calculado: {ovCalc.semEntregas})
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    value={ovForm.semEntregas}
                    onChange={(e) => {
                      setOvWarn("");
                      setOvForm((p) => ({ ...p, semEntregas: e.target.value }));
                    }}
                    onBlur={aplicarComplementoEntregasSem}
                  />
                </div>

                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    Clientes s/ Recolhas (calculado: {ovCalc.semRecolhas})
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    value={ovForm.semRecolhas}
                    onChange={(e) => {
                      setOvWarn("");
                      setOvForm((p) => ({ ...p, semRecolhas: e.target.value }));
                    }}
                    onBlur={aplicarComplementoRecolhasSem}
                  />
                </div>
              </MDBox>

              <div>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Observações</div>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={ovForm.observacoes}
                  onChange={(e) => setOvForm((p) => ({ ...p, observacoes: e.target.value }))}
                />
              </div>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => {
              setOvWarn("");

              // repor valores calculados
              setOvForm((p) => ({
                ...p,
                comEntregas: ovCalc.comEntregas,
                comRecolhas: ovCalc.comRecolhas,
                semEntregas: ovCalc.semEntregas,
                semRecolhas: ovCalc.semRecolhas,
              }));
            }}
            disabled={dlgOvSaving}
          >
            Repor calculado
          </MDButton>

          <MDButton
            color="info"
            onClick={gravarOverride}
            disabled={dlgOvSaving || dlgOvLoading || !ovDirty || !!ovWarn}
          >
            {dlgOvSaving ? "A gravar..." : "Gravar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogVendasTransportesOverrideHistorico
        open={dlgOvHistOpen}
        loading={dlgOvHistLoading}
        rows={dlgOvHistRows}
        reftransporte={dlgOvHistRefTransporte}
        datafim={dlgOvHistDataFim}
        onClose={() => setDlgOvHistOpen(false)}
      />

      <DialogFichaTransporteDetalhe
        open={dlgFichaOpen}
        onClose={() => {
          setDlgFichaOpen(false);
          setDlgFichaRow(null);
        }}
        row={dlgFichaRow}
        onAbrirTransportesRef={abrirTransportesNovoSeparador}
      />

      <Dialog
        open={dlgMeritoOpen}
        onClose={() => setDlgMeritoOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle
          id="draggable-dialog-title-merito"
          sx={{
            cursor: "move",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {dlgMeritoTitulo}
          <IconButton size="small" onClick={() => setDlgMeritoOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {dlgMeritoLoading && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          {!dlgMeritoLoading && (
            <MDBox display="grid" gap={1.25}>
              {dlgMeritoRows.map((item) => (
                <MDBox
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.75,
                    backgroundColor:
                      Number(item?.selecionada || 0) === 1 ? "rgba(25, 118, 210, 0.06)" : "#fff",
                    opacity: Number(item?.inativa || 0) === 1 ? 0.55 : 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Number(item?.selecionada || 0) === 1}
                        disabled
                        size="small"
                      />
                    }
                    label={item.designacao}
                    sx={{ mr: 0 }}
                  />

                  <MDBox
                    sx={{
                      minWidth: 56,
                      textAlign: "right",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#344767",
                    }}
                  >
                    {fmtNum(item.valor, 2)}
                  </MDBox>
                </MDBox>
              ))}

              {dlgMeritoRows.length === 0 && (
                <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
                  Sem caraterísticas encontradas.
                </MDTypography>
              )}
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setDlgMeritoOpen(false)} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
